import {z} from "zod";

/** Plain text with an optional client translation. No HTML or executable UI crosses the wire. */
export const presentationTextSchema = z.object({text: z.string().min(1).max(2000), key: z.string().max(200).optional()});
export type PresentationText = z.infer<typeof presentationTextSchema>;
export const presentationFieldSchema = z.object({
   path: z.array(z.string().max(200)).min(1).max(20),
   label: presentationTextSchema,
   format: z.enum(["text", "multiline", "phone", "email", "date-time", "number", "boolean", "entity", "collection", "json"]).default("text"),
   emphasis: z.enum(["primary", "normal"]).default("normal"),
   /** Resolved by the server when the review is prepared, never by the browser on replay. */
   entityName: z.string().min(1).max(500).optional(),
   choices: z.array(z.object({value: z.union([z.string(), z.number(), z.boolean()]), label: presentationTextSchema})).max(100).optional(),
});
export type PresentationField = z.infer<typeof presentationFieldSchema>;

export const toolPresentationSchema = z.object({
   schemaVersion: z.literal(1),
   toolName: z.string().min(1).max(200),
   kind: z.enum(["action", "diff", "collection", "details"]),
   title: presentationTextSchema,
   description: presentationTextSchema.optional(),
   icon: z.enum(["phone", "message", "document", "workflow", "people", "settings", "delete", "tool"]).default("tool"),
   approveLabel: presentationTextSchema.optional(),
   /** A successful tool result may only mean an asynchronous request was accepted. */
   completion: z.enum(["accepted", "completed"]).default("completed"),
   fields: z.array(presentationFieldSchema).max(200),
});
export type ToolPresentation = z.infer<typeof toolPresentationSchema>;

export const toolPresentationBindingSchema = z.object({
   toolCallId: z.string().min(1),
   presentation: z.unknown(),
});

/** Ignore unbound display data; retain identifiable malformed reviews so approval stays disabled. */
export function readToolPresentationBinding(value: unknown) {
   if (!isPresentationRecord(value) || typeof value.toolCallId !== "string" || !value.toolCallId) return;
   const parsed = toolPresentationBindingSchema.safeParse(value);
   // Null distinguishes a supplied but unreadable review from absent legacy metadata.
   return {toolCallId: value.toolCallId, presentation: parsed.success ? parsed.data.presentation ?? null : null};
}

export const presentationIcons = {
   phone: "phone-arrow-up-right", message: "comment", document: "file-lines", workflow: "diagram-project",
   people: "users", settings: "gear", delete: "trash", tool: "wrench",
} as const;

export function presentationText(value: PresentationText, t: (key: string, options?: Record<string, unknown>) => string) {
   return value.key ? t(value.key, {defaultValue: value.text}) : value.text;
}

export function humanizeToolField(value: string) {
   const text = value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim();
   return text.charAt(0).toUpperCase() + text.slice(1);
}

export const isPresentationRecord = (value: unknown): value is Record<string, unknown> =>
   !!value && typeof value === "object" && !Array.isArray(value);

export function presentationValue(input: unknown, path: readonly string[]): {exists: boolean; value: unknown} {
   let value = input;
   for (const segment of path) {
      if ((!isPresentationRecord(value) && !Array.isArray(value)) || !Object.hasOwn(value, segment)) return {exists: false, value: undefined};
      value = (value as Record<string, unknown>)[segment];
   }
   return {exists: true, value};
}

/** Overlapping paths could hide an unreviewed sibling. Every annotated path must be present. */
export function validateToolPresentation(value: unknown, toolName: string, input: unknown): ToolPresentation | undefined {
   const parsed = toolPresentationSchema.safeParse(value);
   if (!parsed.success || parsed.data.toolName !== toolName) return;
   const fields = parsed.data.fields;
   if (fields.some((field, index) => !presentationValue(input, field.path).exists || fields.some((other, otherIndex) =>
      otherIndex !== index && field.path.length <= other.path.length && field.path.every((part, i) => other.path[i] === part)))) return;
   return parsed.data;
}

export type PresentedField = PresentationField & {value: unknown};

/** Preserve unannotated fields and empty values; the renderer decides which fields to display. */
export function presentedFields(input: unknown, fields: readonly PresentationField[]): PresentedField[] {
   const result: PresentedField[] = fields.flatMap(field => {
      const entry = presentationValue(input, field.path);
      return entry.exists ? [{...field, value: entry.value}] : [];
   });
   const visit = (value: unknown, path: string[]) => {
      if (fields.some(field => field.path.length === path.length && field.path.every((part, i) => path[i] === part))) return;
      if (isPresentationRecord(value) && Object.keys(value).length && path.length < 20) {
         Object.entries(value).forEach(([key, child]) => visit(child, [...path, key]));
         return;
      }
      if (!path.length) return;
      const labelPath = path[0] === "body" && path.length > 1 ? path.slice(1) : path;
      result.push({path, value, label: {text: labelPath.map(humanizeToolField).join(" / ")},
         emphasis: "normal", format: Array.isArray(value) ? "collection" : typeof value === "boolean" ? "boolean" : "text"});
   };
   visit(input, []);
   return result;
}

/** Legacy tools and new, unannotated tools share a readable, lossless fallback. */
export function fallbackToolPresentation(toolName: string): ToolPresentation {
   return {schemaVersion: 1, toolName, kind: "details", title: {text: humanizeToolField(toolName)},
      icon: "tool", completion: "completed", fields: []};
}
